import React, { Component } from 'react';
import { AlertTriangle, Check, X, ArrowRight, FileText, ShieldCheck, Lock, CreditCard, Clock, MapPin } from 'lucide-react';
// --- UI Components ---
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-6 md:px-8 ${className}`} data-id="element-2">
    <div className="max-w-3xl mx-auto" data-id="element-3">{children}</div>
  </section>;
const Button = ({
  children,
  className = '',
  variant = 'primary',
  href
}: {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
  href?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-bold text-lg px-8 py-4 w-full md:w-auto transition-all duration-200 border-2';
  const variants = {
    primary: 'bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800 hover:border-zinc-800 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.2)] hover:translate-x-[2px] hover:translate-y-[2px]',
    secondary: 'bg-white text-zinc-900 border-zinc-900 hover:bg-zinc-50 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.2)] hover:translate-x-[2px] hover:translate-y-[2px]'
  };
  const classes = `${baseStyles} ${variants[variant]} ${className}`;
  if (href) {
    return <a href={href} className={classes} data-id="element-4">
        {children}
      </a>;
  }
  return <button className={classes} data-id="element-5">{children}</button>;
};
const Card = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`bg-white border-2 border-zinc-200 p-6 md:p-8 ${className}`} data-id="element-6">
    {children}
  </div>;
const CheckItem = ({
  children
}: {
  children: React.ReactNode;
}) => <li className="flex items-start gap-3 mb-3" data-id="element-7">
    <div className="mt-1 min-w-5" data-id="element-8">
      <Check className="w-5 h-5 text-emerald-600 font-bold" strokeWidth={3} data-id="element-9" />
    </div>
    <span className="text-zinc-700 font-medium leading-relaxed" data-id="element-10">
      {children}
    </span>
  </li>;
const XItem = ({
  children
}: {
  children: React.ReactNode;
}) => <li className="flex items-start gap-3 mb-3" data-id="element-11">
    <div className="mt-1 min-w-5" data-id="element-12">
      <X className="w-5 h-5 text-red-600 font-bold" strokeWidth={3} data-id="element-13" />
    </div>
    <span className="text-zinc-600 leading-relaxed" data-id="element-14">{children}</span>
  </li>;
// --- Main Page Component ---
export function LandingPage() {
  return <div className="min-h-screen bg-zinc-50 text-zinc-900 font-sans selection:bg-yellow-200 selection:text-black" data-id="element-15">
      {/* 1. HERO */}
      <header className="pt-20 pb-16 px-6 md:px-8 bg-white border-b border-zinc-200" data-id="element-16">
        <div className="max-w-3xl mx-auto text-center" data-id="element-17">
          <div className="inline-block bg-zinc-100 px-3 py-1 text-xs font-bold tracking-widest uppercase text-zinc-500 mb-6 border border-zinc-200" data-id="element-18">
            Informe Confidencial para Familias Militares
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight text-zinc-900 mb-6 leading-[1.1]" data-id="element-19">
            TU VIDA FINANCIERA DEPENDE DEL SUELDO DE UNA SOLA PERSONA.
          </h1>
          <p className="text-xl md:text-2xl font-medium text-zinc-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-20">
            Eso no es seguridad.{' '}
            <span className="text-red-600 font-bold bg-red-50 px-1" data-id="element-21">
              Es una bomba de tiempo.
            </span>{' '}
            Descubre el sistema operativo para generar ingresos remotos sin
            depender de las Fuerzas Armadas.
          </p>
          <div className="flex flex-col items-center gap-4" data-id="element-22">
            <Button className="w-full md:w-auto text-xl" href="https://glowliftessencemx.com/checkouts/cn/hWN7pRrFzWq7MULOEmRMcPFi/es-us?_r=AQABpBUK4pC6wZVp_ihZmda-BvmeqjREhKWA3FxBo8HwUY4&preview_theme_id=184489181460&skip_shop_pay=true" data-id="element-23">
              DESCARGAR EL PROTOCOLO ($14.99)
            </Button>
            <p className="text-sm text-zinc-500 flex items-center gap-2" data-id="element-24">
              <Lock className="w-3 h-3" data-id="element-25" /> Pago seguro · Garantía de
              satisfacción · PDF Instantáneo
            </p>
          </div>
        </div>
      </header>

      {/* 2. INTRODUCCIÓN EMOCIONAL */}
      <Section className="bg-zinc-50" data-id="element-26">
        <div className="prose prose-lg prose-zinc mx-auto" data-id="element-27">
          <p className="font-bold text-zinc-900" data-id="element-28">DE: Un Operador Financiero</p>
          <p className="font-bold text-zinc-900" data-id="element-29">
            PARA: Esposas y esposos de militares
          </p>
          <div className="w-12 h-1 bg-zinc-900 my-6" data-id="element-30"></div>

          <p data-id="element-31">
            Te mudas. Pierdes amigos. Pierdes tu trabajo. Llegas a una nueva
            base en medio de la nada. Y empiezas de cero. Otra vez.
          </p>
          <p data-id="element-32">
            Mientras tanto, la inflación sube y el sueldo militar se queda
            igual. Miras tu cuenta bancaria a fin de mes y sientes ese nudo en
            el estómago. Esa ansiedad silenciosa que no le cuentas a nadie para
            no preocupar a tu pareja, que ya tiene suficiente presión en la
            unidad.
          </p>
          <p className="font-bold bg-yellow-100 p-2 inline-block -rotate-1" data-id="element-33">
            "El Army cuidará de nosotros", te dicen.
          </p>
          <p data-id="element-34">
            Mentira. El Army cuida la misión. Tú eres un daño colateral en su
            logística.
          </p>
        </div>
      </Section>

      {/* 3. EL PROBLEMA REAL */}
      <Section className="bg-white border-y border-zinc-200" data-id="element-35">
        <div className="bg-red-50 border-l-4 border-red-600 p-6 md:p-8 mb-10" data-id="element-36">
          <h2 className="text-2xl font-bold text-red-900 mb-4 flex items-center gap-3" data-id="element-37">
            <AlertTriangle className="w-6 h-6" data-id="element-38" />
            LA VERDAD INCÓMODA
          </h2>
          <p className="text-red-800 font-medium text-lg mb-4" data-id="element-39">
            "Mudarte cada 3 años no es el problema. No tener ingresos remotos
            sí."
          </p>
          <p className="text-red-800" data-id="element-40">
            Diseñar tu vida alrededor del uniforme de otra persona es la
            estrategia financiera más peligrosa que existe.
          </p>
        </div>

        <h3 className="text-3xl font-bold mb-8 text-center" data-id="element-41">
          El ciclo de la dependencia
        </h3>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-42">
          <Card data-id="element-43">
            <h4 className="font-bold text-lg mb-2" data-id="element-44">La Trampa del PCS</h4>
            <p className="text-zinc-600 text-sm" data-id="element-45">
              Consigues un trabajo local. Te ascienden. Llegan las órdenes.
              Renuncias. Vuelves a empezar con sueldo mínimo en el siguiente
              estado.
            </p>
          </Card>
          <Card data-id="element-46">
            <h4 className="font-bold text-lg mb-2" data-id="element-47">
              La Amenaza del ETS/Discharge
            </h4>
            <p className="text-zinc-600 text-sm" data-id="element-48">
              Un día el uniforme se cuelga. Si no construiste tu propio capital
              y carrera antes de ese día, la caída financiera es brutal.
            </p>
          </Card>
        </div>

        <p className="text-center mt-10 text-xl font-bold text-zinc-900" data-id="element-49">
          El Army no es un plan financiero. Es un empleo temporal con alto
          riesgo.
        </p>
      </Section>

      {/* 4. POR QUÉ LO QUE PROBASTE FALLÓ */}
      <Section className="bg-zinc-100" data-id="element-50">
        <h2 className="text-3xl font-black text-center mb-12 uppercase tracking-tight" data-id="element-51">
          Por qué has fallado hasta ahora
        </h2>
        <div className="space-y-4 max-w-2xl mx-auto" data-id="element-52">
          <div className="flex gap-4 items-start bg-white p-4 border border-zinc-200" data-id="element-53">
            <X className="w-6 h-6 text-red-500 shrink-0 mt-1" data-id="element-54" />
            <div data-id="element-55">
              <h4 className="font-bold" data-id="element-56">MLMs y Pirámides</h4>
              <p className="text-sm text-zinc-600" data-id="element-57">
                Te prometen libertad, pero terminas acosando a tus amigos para
                venderles batidos o aceites. Eso no es un negocio, es una secta
                comercial.
              </p>
            </div>
          </div>
          <div className="flex gap-4 items-start bg-white p-4 border border-zinc-200" data-id="element-58">
            <X className="w-6 h-6 text-red-500 shrink-0 mt-1" data-id="element-59" />
            <div data-id="element-60">
              <h4 className="font-bold" data-id="element-61">Dropshipping y Amazon FBA</h4>
              <p className="text-sm text-zinc-600" data-id="element-62">
                Requieren capital, inventario y márgenes ridículos. ¿Tienes
                $5,000 para arriesgar en stock chino?
              </p>
            </div>
          </div>
          <div className="flex gap-4 items-start bg-white p-4 border border-zinc-200" data-id="element-63">
            <X className="w-6 h-6 text-red-500 shrink-0 mt-1" data-id="element-64" />
            <div data-id="element-65">
              <h4 className="font-bold" data-id="element-66">Trabajos Remotos "Tradicionales"</h4>
              <p className="text-sm text-zinc-600" data-id="element-67">
                Competir contra 500 personas por un puesto de Customer Service
                de $12/hora que te exige horario fijo. Imposible con niños y
                caos militar.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 5. LA SOLUCIÓN */}
      <Section className="bg-zinc-900 text-white" data-id="element-68">
        <div className="text-center mb-12" data-id="element-69">
          <span className="text-emerald-400 font-bold tracking-widest uppercase text-sm" data-id="element-70">
            La Nueva Estrategia
          </span>
          <h2 className="text-3xl md:text-5xl font-black mt-4 mb-6" data-id="element-71">
            INGRESO PORTÁTIL™
          </h2>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-72">
            No te falta tiempo. Te falta un sistema que funcione dentro de tu
            realidad militar.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 text-center" data-id="element-73">
          <div className="p-6 border border-zinc-700 bg-zinc-800/50" data-id="element-74">
            <MapPin className="w-10 h-10 text-emerald-400 mx-auto mb-4" data-id="element-75" />
            <h3 className="font-bold text-lg mb-2" data-id="element-76">
              100% Agnóstico de Ubicación
            </h3>
            <p className="text-zinc-400 text-sm" data-id="element-77">
              Funciona en Fort Bragg, en Alemania, en Japón o en la casa de tus
              suegros.
            </p>
          </div>
          <div className="p-6 border border-zinc-700 bg-zinc-800/50" data-id="element-78">
            <Clock className="w-10 h-10 text-emerald-400 mx-auto mb-4" data-id="element-79" />
            <h3 className="font-bold text-lg mb-2" data-id="element-80">Asíncrono por Diseño</h3>
            <p className="text-zinc-400 text-sm" data-id="element-81">
              Sin horarios fijos. Trabajas cuando los niños duermen o cuando el
              caos baja.
            </p>
          </div>
          <div className="p-6 border border-zinc-700 bg-zinc-800/50" data-id="element-82">
            <CreditCard className="w-10 h-10 text-emerald-400 mx-auto mb-4" data-id="element-83" />
            <h3 className="font-bold text-lg mb-2" data-id="element-84">Alto Valor, Bajo Costo</h3>
            <p className="text-zinc-400 text-sm" data-id="element-85">
              Vendes servicios digitales simples a negocios reales. Cero
              inventario. Cero envío.
            </p>
          </div>
        </div>
      </Section>

      {/* 6. PREVIEW DEL PDF */}
      <Section className="bg-white" data-id="element-86">
        <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-87">
          <div className="w-full md:w-1/2 bg-zinc-100 p-8 border-2 border-zinc-900 shadow-[8px_8px_0px_0px_rgba(24,24,27,1)] aspect-[3/4] flex flex-col justify-center items-center text-center" data-id="element-88">
            <FileText className="w-20 h-20 text-zinc-900 mb-6" data-id="element-89" />
            <h3 className="text-2xl font-black uppercase leading-none mb-2" data-id="element-90">
              Ingreso Remoto
            </h3>
            <p className="font-serif italic text-zinc-600 mb-8" data-id="element-91">
              Para Familias Militares
            </p>
            <div className="w-full h-1 bg-zinc-900 mb-2" data-id="element-92"></div>
            <div className="w-full h-1 bg-zinc-900 mb-2 opacity-50" data-id="element-93"></div>
            <div className="w-full h-1 bg-zinc-900 opacity-25" data-id="element-94"></div>
            <p className="mt-8 font-bold text-xl" data-id="element-95">$14.99 USD</p>
          </div>

          <div className="w-full md:w-1/2" data-id="element-96">
            <h2 className="text-3xl font-black mb-6" data-id="element-97">
              NO ES UN LIBRO. ES UN MANUAL DE OPERACIONES.
            </h2>
            <p className="text-lg text-zinc-600 mb-8" data-id="element-98">
              Este PDF de 60 páginas elimina la grasa. No hay historias
              inspiradoras de mi infancia. Solo tácticas de guerrilla para
              generar tus primeros $3,000-$5,000 mensuales desde casa.
            </p>
            <ul className="space-y-4" data-id="element-99">
              <CheckItem data-id="element-100">
                Cómo identificar habilidades que YA tienes y monetizarlas.
              </CheckItem>
              <CheckItem data-id="element-101">
                El modelo "Service Arbitrage": Conectar oferta y demanda sin
                hacer el trabajo pesado.
              </CheckItem>
              <CheckItem data-id="element-102">
                Lead Generation & Appointment Setting: Las dos habilidades que
                todo negocio necesita.
              </CheckItem>
              <CheckItem data-id="element-103">
                Guiones exactos para conseguir clientes sin parecer desesperado.
              </CheckItem>
            </ul>
          </div>
        </div>
      </Section>

      {/* 7. QUÉ INCLUYE */}
      <Section className="bg-zinc-50 border-y border-zinc-200" data-id="element-104">
        <h2 className="text-3xl font-black text-center mb-12" data-id="element-105">
          ARSENAL INCLUIDO
        </h2>
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-106">
          {['Módulo 1: Desprogramación Mental (Adiós a la mentalidad de dependiente)', 'Módulo 2: El Mapa del Tesoro Digital (Dónde está el dinero real)', 'Módulo 3: Selección de Tu Vehículo (Lead Gen, Copy, Admin)', 'Módulo 4: Configuración de la Base (Herramientas gratuitas)', 'Módulo 5: Adquisición de Objetivos (Cómo encontrar clientes)', 'BONUS: Plan de Acción de 14 Días (Día por día, tarea por tarea)'].map((item, i) => <div key={i} className="bg-white p-4 border border-zinc-200 font-medium flex items-center gap-3" data-id="element-107">
              <span className="bg-zinc-900 text-white w-6 h-6 flex items-center justify-center text-xs font-bold rounded-sm shrink-0" data-id="element-108">
                {i + 1}
              </span>
              {item}
            </div>)}
        </div>
      </Section>

      {/* 8. ES PARA TI / NO ES PARA TI */}
      <Section className="bg-white" data-id="element-109">
        <div className="grid md:grid-cols-2 gap-12" data-id="element-110">
          <div data-id="element-111">
            <h3 className="text-xl font-black uppercase mb-6 flex items-center gap-2 text-emerald-700" data-id="element-112">
              <Check className="w-6 h-6" data-id="element-113" /> ESTO ES PARA TI SI...
            </h3>
            <ul className="space-y-4" data-id="element-114">
              <CheckItem data-id="element-115">
                Eres esposa/o de militar y vives en la incertidumbre.
              </CheckItem>
              <CheckItem data-id="element-116">
                Estás harto/a de depender de un solo sueldo que apenas alcanza.
              </CheckItem>
              <CheckItem data-id="element-117">
                Quieres una carrera que quepa en tu mochila cuando llegue el
                PCS.
              </CheckItem>
              <CheckItem data-id="element-118">
                Estás dispuesto a trabajar duro si el sistema tiene sentido.
              </CheckItem>
            </ul>
          </div>
          <div data-id="element-119">
            <h3 className="text-xl font-black uppercase mb-6 flex items-center gap-2 text-red-700" data-id="element-120">
              <X className="w-6 h-6" data-id="element-121" /> ESTO NO ES PARA TI SI...
            </h3>
            <ul className="space-y-4" data-id="element-122">
              <XItem data-id="element-123">Buscas dinero fácil o mágico sin esfuerzo.</XItem>
              <XItem data-id="element-124">Odias vender o comunicarte con otras personas.</XItem>
              <XItem data-id="element-125">
                Crees que el mundo te debe algo por ser familia militar.
              </XItem>
              <XItem data-id="element-126">
                No estás dispuesto a aprender habilidades digitales básicas.
              </XItem>
            </ul>
          </div>
        </div>
      </Section>

      {/* 9. CÓMO FUNCIONA */}
      <Section className="bg-zinc-900 text-white" data-id="element-127">
        <h2 className="text-3xl font-black text-center mb-12" data-id="element-128">
          MISIÓN EN 3 PASOS
        </h2>
        <div className="grid md:grid-cols-3 gap-8 text-center relative" data-id="element-129">
          {/* Connector Line (Desktop) */}
          <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-zinc-700 -z-10" data-id="element-130"></div>

          <div className="bg-zinc-900 p-4" data-id="element-131">
            <div className="w-24 h-24 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-zinc-900 text-3xl font-black" data-id="element-132">
              1
            </div>
            <h3 className="text-xl font-bold mb-2" data-id="element-133">ADQUIERE</h3>
            <p className="text-zinc-400" data-id="element-134">
              Descarga el PDF y lee la realidad del mercado actual. Entiende las
              reglas del juego.
            </p>
          </div>
          <div className="bg-zinc-900 p-4" data-id="element-135">
            <div className="w-24 h-24 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-zinc-900 text-3xl font-black" data-id="element-136">
              2
            </div>
            <h3 className="text-xl font-bold mb-2" data-id="element-137">IMPLEMENTA</h3>
            <p className="text-zinc-400" data-id="element-138">
              Sigue el plan de 14 días. Configura tu oferta y tus sistemas de
              cobro.
            </p>
          </div>
          <div className="bg-zinc-900 p-4" data-id="element-139">
            <div className="w-24 h-24 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-zinc-900 text-3xl font-black" data-id="element-140">
              3
            </div>
            <h3 className="text-xl font-bold mb-2" data-id="element-141">EJECUTA</h3>
            <p className="text-zinc-400" data-id="element-142">
              Contacta a tus primeros prospectos usando los guiones probados.
              Cierra el trato.
            </p>
          </div>
        </div>
      </Section>

      {/* 10. FAQ */}
      <Section className="bg-zinc-50" data-id="element-143">
        <h2 className="text-3xl font-black text-center mb-12" data-id="element-144">
          PREGUNTAS FRECUENTES
        </h2>
        <div className="max-w-2xl mx-auto space-y-6" data-id="element-145">
          {[{
          q: '¿Esto es trading o cripto?',
          a: 'No. Absolutamente no. Eso es apostar. Esto es ofrecer servicios reales a empresas reales a cambio de dinero.'
        }, {
          q: '¿Es dropshipping o Amazon?',
          a: 'No. Esos modelos requieren capital de riesgo. Aquí vendes tu habilidad y tiempo, no productos físicos.'
        }, {
          q: '¿Cuánto tiempo toma?',
          a: 'El plan de 14 días está diseñado para dedicarle 60-90 minutos al día. Una vez establecido, tú decides cuánto escalar.'
        }, {
          q: '¿Necesito experiencia previa?',
          a: 'No necesitas un título. Necesitas saber usar una computadora, escribir correos y tener sentido común.'
        }, {
          q: '¿Funciona con hijos y mudanzas?',
          a: 'Ese es el punto entero. Está diseñado ESPECÍFICAMENTE para sobrevivir a hijos gritando y cajas de mudanza.'
        }].map((faq, i) => <div key={i} className="bg-white border border-zinc-200 p-6" data-id="element-146">
              <h4 className="font-bold text-lg mb-2" data-id="element-147">{faq.q}</h4>
              <p className="text-zinc-600" data-id="element-148">{faq.a}</p>
            </div>)}
        </div>
      </Section>

      {/* 11. CIERRE EMOCIONAL */}
      <Section className="bg-white text-center" data-id="element-149">
        <div className="max-w-2xl mx-auto" data-id="element-150">
          <h2 className="text-3xl md:text-4xl font-black mb-6" data-id="element-151">
            NO ESPERES A LA CRISIS
          </h2>
          <p className="text-lg text-zinc-600 mb-8 leading-relaxed" data-id="element-152">
            El mejor momento para cavar un pozo es antes de tener sed. El mejor
            momento para construir un segundo ingreso es antes de que llegue la
            orden de separación o el despido médico.
          </p>
          <p className="text-lg font-bold text-zinc-900 mb-10" data-id="element-153">
            Tener ingresos remotos no es un lujo en 2026. Es supervivencia
            moderna.
          </p>

          <div className="bg-zinc-50 p-8 border-2 border-zinc-900 inline-block w-full max-w-md" data-id="element-154">
            <div className="flex justify-between items-center mb-4 border-b border-zinc-200 pb-4" data-id="element-155">
              <span className="font-bold text-zinc-500" data-id="element-156">PRODUCTO</span>
              <span className="font-bold text-zinc-900" data-id="element-157">MANUAL PDF</span>
            </div>
            <div className="flex justify-between items-center mb-8" data-id="element-158">
              <span className="font-bold text-zinc-500" data-id="element-159">PRECIO</span>
              <span className="text-4xl font-black text-zinc-900" data-id="element-160">$14.99</span>
            </div>
            <Button className="w-full mb-4" href="https://glowliftessencemx.com/checkouts/cn/hWN7pRrFzWq7MULOEmRMcPFi/es-us?_r=AQABpBUK4pC6wZVp_ihZmda-BvmeqjREhKWA3FxBo8HwUY4&preview_theme_id=184489181460&skip_shop_pay=true" data-id="element-161">
              COMPRAR AHORA
            </Button>
            <div className="flex items-center justify-center gap-2 text-xs text-zinc-500 font-medium" data-id="element-162">
              <ShieldCheck className="w-4 h-4 text-emerald-600" data-id="element-163" />
              GARANTÍA DE SATISFACCIÓN DE 30 DÍAS
            </div>
          </div>
        </div>
      </Section>

      {/* 12. LEGAL */}
      <footer className="bg-zinc-900 text-zinc-500 py-12 px-6 text-xs leading-relaxed text-center" data-id="element-164">
        <div className="max-w-3xl mx-auto space-y-4" data-id="element-165">
          <p data-id="element-166">
            AVISO LEGAL Este producto es solo informativo y educativo. No
            constituye asesoría financiera, legal, fiscal ni laboral. No se
            garantizan resultados ni ingresos; los resultados dependen del
            esfuerzo, habilidades y ejecución de cada persona. Si necesitas
            ayuda profesional (financiera, legal o de salud mental), consulta a
            un especialista autorizado.
          </p>
          <p data-id="element-167">
            © 2026 Ingreso Portátil™ para Familias Militares. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}